import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  ActivityIndicator,
  Dimensions,
  ScrollView,
} from 'react-native';
import { useAuth } from '../services/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import CheckerPattern from '../components/CheckerPattern';

const { width } = Dimensions.get('window');

const ForgotPasswordScreen = ({ navigation, route }) => {
  const initialEmail = route?.params?.email || '';
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'success'
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const { forgotPassword, forgotPasswordSubmit } = useAuth();
  const { isDarkMode } = useTheme();

  // Theme colors
  const theme = {
    bg: isDarkMode ? '#0A0A0A' : '#FAFAFA',
    card: isDarkMode ? '#141414' : '#FFFFFF',
    text: isDarkMode ? '#FFFFFF' : '#0A0A0A',
    textSecondary: isDarkMode ? '#888888' : '#666666',
    border: isDarkMode ? '#2A2A2A' : '#E5E5E5',
    inputBg: isDarkMode ? '#1A1A1A' : '#F5F5F5',
    accent: '#94EB98',
    accentDark: '#7BC97F',
    error: '#FF6B6B',
    success: '#4CAF50',
  };

  const handleSendCode = async () => {
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }

    setErrorMessage('');
    setLoading(true);

    try {
      const result = await forgotPassword(email);
      setLoading(false);

      if (result.success) {
        setStep('code');
      } else {
        if (result.error?.includes('User not found') || result.error?.includes('UserNotFoundException')) {
          setErrorMessage('No account found with this email address.');
        } else {
          setErrorMessage(result.error || 'Failed to send reset code. Please try again.');
        }
      }
    } catch (error) {
      setLoading(false);
      setErrorMessage(error.message || 'An error occurred');
    }
  };

  const handleResetPassword = async () => {
    if (!code) {
      setErrorMessage('Please enter the verification code');
      return;
    }
    if (!newPassword) {
      setErrorMessage('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    setErrorMessage('');
    setLoading(true);

    try {
      const result = await forgotPasswordSubmit(email, code, newPassword);
      setLoading(false);

      if (result.success) {
        setStep('success');
      } else {
        if (result.error?.includes('Invalid code') || result.error?.includes('CodeMismatchException')) {
          setErrorMessage('Invalid verification code. Please try again.');
        } else if (result.error?.includes('expired')) {
          setErrorMessage('Code has expired. Please request a new one.');
        } else {
          setErrorMessage(result.error || 'Failed to reset password. Please try again.');
        }
      }
    } catch (error) {
      setLoading(false);
      setErrorMessage(error.message || 'An error occurred');
    }
  };

  const handleBackToLogin = () => {
    navigation.navigate('Login');
  };

  const renderEmailStep = () => (
    <>
      <View style={styles.header}>
        <Text style={[styles.title, { color: theme.text }]}>
          Reset Password
        </Text>
        <Text style={[styles.subtitle, { color: theme.textSecondary }]}>
          Enter your email address and we'll send you a verification code to reset your password.
        </Text>
      </View>

      {errorMessage ? (
        <View style={[styles.errorContainer, { backgroundColor: theme.error + '15' }]}>
          <Text style={[styles.errorText, { color: theme.error }]}>
            {errorMessage}
          </Text>
        </View>
      ) : null}

      <View style={styles.inputContainer}>
        <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>
          Email
        </Text>
        <TextInput
          style={[
            styles.input,
            { color: theme.text, borderBottomColor: theme.border },
          ]}
          placeholder="Enter your email"
          placeholderTextColor={theme.textSecondary}
          value={email}
          onChangeText={setEmail}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
        />
      </View>

      <Pressable
        style={({ pressed }) => [
          styles.primaryButton,
          { backgroundColor: theme.accent },
          pressed && styles.buttonPressed,
          loading && styles.buttonDisabled,
        ]}
        onPress={handleSendCode}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator color="#000" />
        ) : (
          <Text style={styles.primaryButtonText}>Send Reset Code</Text>
        )}
      </Pressable>

      <Pressable
        style={({ pressed }) => [
          styles.secondaryButton,
          { borderColor: theme.border },
          pressed && styles.buttonPressed,
        ]}
        onPress={handleBackToLogin}
      >
        <Text style={[styles.secondaryButtonText, { color: theme.text }]}>
          Back to Login
        </Text>
      </Pressable>
    </>
  );

  const renderCodeStep = () => (
    <>
      <View style={styles.header}>
        <Text style={[styles.title, { color: theme.text }]}>
          Enter Code
        </Text>
        <Text style={[styles.subtitle, { color: theme.textSecondary }]}>
          We've sent a verification code to{' '}
          <Text style={{ fontWeight: '600', color: theme.text }}>{email}</Text>
        </Text>
      </View>

      {errorMessage ? (
        <View style={[styles.errorContainer, { backgroundColor: theme.error + '15' }]}>
          <Text style={[styles.errorText, { color: theme.error }]}>
            {errorMessage}
          </Text>
        </View>
      ) : null}

      <View style={styles.inputContainer}>
        <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>
          Verification Code
        </Text>
        <TextInput
          style={[
            styles.input,
            { color: theme.text, borderBottomColor: theme.border },
          ]}
          placeholder="Enter 6-digit code"
          placeholderTextColor={theme.textSecondary}
          value={code}
          onChangeText={setCode}
          keyboardType="number-pad"
          maxLength={6}
        />
      </View>

      <View style={styles.inputContainer}>
        <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>
          New Password
        </Text>
        <TextInput
          style={[
            styles.input,
            { color: theme.text, borderBottomColor: theme.border },
          ]}
          placeholder="Enter new password"
          placeholderTextColor={theme.textSecondary}
          value={newPassword}
          onChangeText={setNewPassword}
          secureTextEntry
          autoCapitalize="none"
        />
      </View>

      <View style={styles.inputContainer}>
        <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>
          Confirm Password
        </Text>
        <TextInput
          style={[
            styles.input,
            { color: theme.text, borderBottomColor: theme.border },
          ]}
          placeholder="Confirm new password"
          placeholderTextColor={theme.textSecondary}
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          secureTextEntry
          autoCapitalize="none"
        />
      </View>

      <Pressable
        style={({ pressed }) => [
          styles.primaryButton,
          { backgroundColor: theme.accent },
          pressed && styles.buttonPressed,
          loading && styles.buttonDisabled,
        ]}
        onPress={handleResetPassword}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator color="#000" />
        ) : (
          <Text style={styles.primaryButtonText}>Reset Password</Text>
        )}
      </Pressable>

      <Pressable
        style={({ pressed }) => [
          styles.textButton,
          pressed && styles.buttonPressed,
        ]}
        onPress={() => {
          setStep('email');
          setCode('');
          setNewPassword('');
          setConfirmPassword('');
          setErrorMessage('');
        }}
      >
        <Text style={[styles.textButtonText, { color: theme.accent }]}>
          Didn't receive code? Try again
        </Text>
      </Pressable>
    </>
  );

  const renderSuccessStep = () => (
    <>
      <View style={styles.header}>
        <View style={styles.successIcon}>
          <Text style={{ fontSize: 48 }}>✓</Text>
        </View>
        <Text style={[styles.title, { color: theme.success }]}>
          Password Reset!
        </Text>
        <Text style={[styles.subtitle, { color: theme.textSecondary }]}>
          Your password has been successfully reset. You can now sign in with your new password.
        </Text>
      </View>

      <Pressable
        style={({ pressed }) => [
          styles.primaryButton,
          { backgroundColor: theme.accent },
          pressed && styles.buttonPressed,
        ]}
        onPress={handleBackToLogin}
      >
        <Text style={styles.primaryButtonText}>Back to Login</Text>
      </Pressable>
    </>
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.bg }]}>
      <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
      
      {/* Background Pattern */}
      <View style={styles.patternContainer}>
        <CheckerPattern />
      </View>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={[styles.card, { backgroundColor: theme.card }]}>
            {step === 'email' && renderEmailStep()}
            {step === 'code' && renderCodeStep()}
            {step === 'success' && renderSuccessStep()}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  patternContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.03,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    borderRadius: 24,
    padding: 32,
    maxWidth: 400,
    width: '100%',
    alignSelf: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  header: {
    marginBottom: 24,
    alignItems: 'center',
  },
  successIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(76, 175, 80, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
  },
  errorContainer: {
    padding: 12,
    borderRadius: 12,
    marginBottom: 16,
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    height: 52,
    borderRadius: 12,
    paddingHorizontal: 16,
    fontSize: 16,
    borderWidth: 0,
    borderBottomWidth: 1,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && { outline: 'none' }),
  },
  primaryButton: {
    height: 52,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  primaryButtonText: {
    color: '#000',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    height: 52,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  textButton: {
    padding: 12,
    alignItems: 'center',
  },
  textButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  buttonPressed: {
    opacity: 0.8,
    transform: [{ scale: 0.98 }],
  },
  buttonDisabled: {
    opacity: 0.6,
  },
});

export default ForgotPasswordScreen;
